import { useConfirmationDialog } from '@openx/components-core/lib/Dialog/ConfirmationDialog/useConfirmationDialog.tsx';
import { useOrganizationContext } from '@openx/components-organization/lib/utils/context/OrganizationContext.tsx';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CreateApiKeyConfirmationModalContent } from '../components/CreateApiKeyConfirmationModalContent.tsx';
import { CreateApiKeyModalContent } from '../components/CreateApiKeyModalContent.tsx';

export type CreateApiKeyModalForm = {
  apiKeyName: string;
};

export const useCreateApiKeyModal = () => {
  const { t } = useTranslation();
  const { open, setLoading, close } = useConfirmationDialog();
  const { useApiKeyCreate } = useOrganizationContext();
  const { createApiKey } = useApiKeyCreate();

  const formMethods = useForm<CreateApiKeyModalForm>({
    defaultValues: {
      apiKeyName: '',
    },
  });

  const openModal = useCallback(
    (apiKey: string) => {
      open({
        cancelLabel: t('Close'),
        content: <CreateApiKeyConfirmationModalContent apiKey={apiKey} />,
        customContent: true,
        hideConfirm: true,
        onConfirmed: () => close(),
        title: t('The API Key Has Been Created'),
      });
    },
    [open, close, t]
  );

  const handleOnConfirmed = useCallback(
    async (id: string) => {
      setLoading(true);

      try {
        const result = await createApiKey(formMethods.getValues('apiKeyName'), id);

        if (result) {
          close();
          openModal(result.apiKey);
          formMethods.reset();
        }
      } finally {
        setLoading(false);
      }
    },
    [close, setLoading, createApiKey, formMethods, openModal]
  );

  return useCallback(
    (id: string) => {
      open({
        confirmationButtonColor: 'primary',
        confirmationLabel: t('Create'),
        content: <CreateApiKeyModalContent formMethods={formMethods} />,
        customContent: true,
        hideConfirm: false,
        onConfirmed: () => handleOnConfirmed(id),
        title: t('Create New API Key'),
      });
    },
    [handleOnConfirmed, open, formMethods, t]
  );
};
